import { EApiStatus, ERROR_TEACHER_BACKEND } from 'constants/common';
import axios from 'plugins/api/axios';
import { IDataExistReferenceSystem, UserType } from 'types/referenceSystem';
import { TeacherRecruitmentDto } from 'src/types/teacherRecruitment';
import { IProfile } from 'src/types/profile';

const {
  oneClubGatewayStaffMember,
  oneClubGateway,
  teacherRecruit,
  teachers,
  referenceSystem,
} = axios;

enum ErrorCode {
  NotFound = 404,
}

export type TTeacherAccountResolution =
  | {
      status: 'ok';
      teacherRecruitData: TeacherRecruitmentDto | undefined;
      globalDataReferenceSystem: IDataExistReferenceSystem;
      typeAccountFromReferenceSystem: string;
    }
  | { status: 'conflict' }
  | { status: 'accountDataAnomaly' }
  | { status: 'backendError'; message: string };

/**
 * 信箱驗證通過後，判斷這個登入中的帳號能不能繼續使用——依序檢查身份衝突（是否已是
 * 一般會員/員工）、教師資料來源是否完整、以及舊格式錯誤碼，回傳單一狀態給呼叫端。
 */
export const resolveTeacherAccount =
  async (): Promise<TTeacherAccountResolution> => {
    let typeAccountFromReferenceSystem = '';
    let globalDataReferenceSystem: IDataExistReferenceSystem = {
      id: '',
      userType: UserType.ONECLUB_ID,
      userId: '',
      referralCode: '',
      referredBy: '',
      createdAt: '',
      updatedAt: '',
      appliedReferralCodeTime: '',
      email: '',
      emailVerified: false,
    };

    const [
      {
        data: dataReferenceSystemTypeOneClubID,
        status: statusReferenceSystemTypeOneClubID,
        error: errorReferenceSystemTypeOneClubID,
      },
      {
        data: oldTeacherData,
        status: oldTeacherStatus,
        error: oldTeacherError,
      },
      { data: teacherRecruitData },
      { data: dataCustomer, status: statusCustomer },
      { data: dataStaffMember, status: statusStaffMember },
    ] = await Promise.all([
      referenceSystem.getByUserIdAndType({ userType: UserType.ONECLUB_ID }),
      teachers.getMe(),
      teacherRecruit.get(),
      oneClubGateway.checkIsACustomer(),
      oneClubGatewayStaffMember.checkIsAStaffMember(),
    ]);

    if (
      dataReferenceSystemTypeOneClubID &&
      errorReferenceSystemTypeOneClubID?.errorCode !== ErrorCode.NotFound &&
      statusReferenceSystemTypeOneClubID === EApiStatus.SUCCESS
    ) {
      typeAccountFromReferenceSystem = UserType.ONECLUB_ID;
      globalDataReferenceSystem = dataReferenceSystemTypeOneClubID;
    }

    const isCustomer =
      dataCustomer !== undefined || statusCustomer === EApiStatus.SUCCESS;
    const isStaffMember =
      dataStaffMember !== undefined || statusStaffMember === EApiStatus.SUCCESS;

    if (isCustomer || isStaffMember) {
      return { status: 'conflict' };
    }

    const existTeacherData =
      oldTeacherStatus === EApiStatus.SUCCESS && !!oldTeacherData;
    const existUsernameInTeacherRecruitment = !!teacherRecruitData;
    const emailForVerification =
      teacherRecruitData?.email ||
      (oldTeacherData as unknown as IProfile)?.email;

    if (
      (!existTeacherData && !existUsernameInTeacherRecruitment) ||
      !emailForVerification
    ) {
      return { status: 'accountDataAnomaly' };
    }

    if (
      !existUsernameInTeacherRecruitment &&
      (oldTeacherError?.errorCode ===
        ERROR_TEACHER_BACKEND.INSTANT_MESSAGING_USER_CREATE_ERROR ||
        oldTeacherError?.errorCode ===
          ERROR_TEACHER_BACKEND.INSTANT_MESSAGING_USER_GET_ERROR ||
        oldTeacherError?.errorCode ===
          ERROR_TEACHER_BACKEND.INSTANT_MESSAGING_USER_UPDATE_ERROR)
    ) {
      return { status: 'backendError', message: `${oldTeacherError?.message}` };
    }

    return {
      status: 'ok',
      teacherRecruitData,
      globalDataReferenceSystem,
      typeAccountFromReferenceSystem,
    };
  };
